import {
  ActivatedRouteSnapshot,
  CanActivate,
  Event as AngularRouterEvent,
  Navigation,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { getTagsFromState } from './wizard-step-state.model';
import { IWizardStep, StepState } from './wizard-step.model';
import { WizardNavigationContext } from '../util/navigation.util';
import { IWizardController } from './wizard-controller.model';
import { WizardContext } from '../service/wizard.context';
import { ConditionCheckResult } from './wizard-condition.model';
import { ComponentRef, Type } from '@angular/core';
import { isPreventableEvent, PreventableEvent } from '../util/util.types';

export type WizardEvent<T extends WizardEventType = WizardEventType> =
  | WizardEventPayload<T>
  | PreventableEvent<WizardEventPayload<T>>
  | { eventType: string };
export type WizardEventPayload<T extends WizardEventType = WizardEventType> = { eventType: T };
export type WizardContextualEventPayload<T extends WizardEventType = WizardEventType> = WizardEventPayload<T> & { context: WizardContext };
export enum WizardEventType {
  ProcessStart = 'processStart',
  ProcessEnd = 'processEnd',
  BeforeProcessEnd = 'beforeProcessEnd',
  ProcessEndPrevented = 'processEndPrevented',
  EnterStep = 'enterStep',
  StepStateChanged = 'stepStateChanged',
  StepCannotBeEntered = 'stepCannotBeEntered',
  GuardEvaluation = 'guardEvaluation',

  // WizardContext Events
  ComponentInstanceCreated = 'componentInstanceCreated',
  ComponentInstanceDestroyed = 'componentInstanceDestroyed',
  // Navigation Events
  BeforeNextStepNavigation = 'beforeNextStepNavigation',
  BeforePrevStepNavigation = 'beforePrevStepNavigation',
  BeforeJumpNavigation = 'beforeJumpNavigation',
  RouterNavigation = 'routerNavigation',
  /** Triggerd if user clicks next / prev */
  ValidateStepBeforeNavigation = 'validateStepNavigation',
  StepNavigationPrevented = 'stepNavigationPrevented',
  StepInvalidOnNavigation = 'stepInvalidOnNavigation',
  StepCannotBeSkipped = 'stepCannotBeSkipped',

  /** Confirm Dialog */
  ConfirmExitDialogResult = 'confirmExitDialogResult',
}
export function normalizeEventToPayload<T = WizardEventPayload>(event: WizardEvent): T {
  return isPreventableEvent(event) ? (event as PreventableEvent<T>).data : (event as T);
}
export function isOfEventType<T extends WizardEvent = WizardEvent>(
  event: WizardEvent,
  type: WizardEventType | WizardEventType[],
): event is T {
  const eventType = normalizeEventToPayload<WizardEventPayload>(event).eventType;
  if (Array.isArray(type)) {
    return type.includes(eventType);
  }
  return eventType === type;
}
/** Simple wrapper for angular router events to pass them over the wizard event chain */
export class RouterNavigationEvent<RE extends AngularRouterEvent = AngularRouterEvent>
  implements WizardEventPayload<WizardEventType.RouterNavigation>
{
  eventType: WizardEventType.RouterNavigation = WizardEventType.RouterNavigation;
  constructor(
    public readonly routerEvent: RE,
    public readonly wizardRoot: InstanceType<Type<unknown>>,
  ) {}
}

export class ProcessStartEvent implements WizardEventPayload<WizardEventType.ProcessStart> {
  eventType: WizardEventType.ProcessStart = WizardEventType.ProcessStart;
  constructor(public controller: IWizardController) {}
}

export class BeforeProcessEndEventPayload implements WizardEventPayload<WizardEventType.BeforeProcessEnd> {
  eventType: WizardEventType.BeforeProcessEnd = WizardEventType.BeforeProcessEnd;
  constructor(
    public controller: IWizardController,
    public reason: ProcessEndEvent['reason'],
  ) {}
}

export type BeforeProcessEndEvent = PreventableEvent<BeforeProcessEndEventPayload>;

/** PROCESS END: If the user finishes or closes the wizard this is called. */
export class ProcessEndEvent implements WizardEventPayload<WizardEventType.ProcessEnd> {
  eventType: WizardEventType.ProcessEnd = WizardEventType.ProcessEnd;
  constructor(
    public controller: IWizardController,
    public reason: 'finished' | 'closed',
    public navigationMetadata: {
      navigation?: Navigation; // if missing, we use navigateBackUrl
      targetUrl: UrlTree;
      targetUrlInWizard: boolean;
    },
  ) {}
}

export class ProcessEndPreventedEvent implements WizardEventPayload<WizardEventType.ProcessEndPrevented> {
  eventType: WizardEventType.ProcessEndPrevented = WizardEventType.ProcessEndPrevented;
  constructor(
    public controller: IWizardController,
    public reason: ProcessEndEvent['reason'],
  ) {}
}

export class ConfirmExitDialogResultEvent implements WizardEventPayload<WizardEventType.ConfirmExitDialogResult> {
  eventType: WizardEventType.ConfirmExitDialogResult = WizardEventType.ConfirmExitDialogResult;
  constructor(
    public controller: IWizardController,
    public shouldContinue: boolean,
    public reason: ProcessEndEvent['reason'],
  ) {}
}

/** ENTER STEP: If a new step is entered */
export class EnterStepEvent implements WizardEventPayload<WizardEventType.EnterStep> {
  eventType: WizardEventType.EnterStep = WizardEventType.EnterStep;
  constructor(
    public step: IWizardStep,
    public type: StepChangeType,
    public fromStep?: IWizardStep,
  ) {}
}

export class ComponentInstanceCreatedEvent<T = unknown> implements WizardContextualEventPayload<WizardEventType.ComponentInstanceCreated> {
  eventType: WizardEventType.ComponentInstanceCreated = WizardEventType.ComponentInstanceCreated;
  constructor(
    public context: WizardContext,
    public step: IWizardStep,
    public reference: ComponentRef<T>,
  ) {}
}

export class ComponentInstanceDestroyedEvent implements WizardContextualEventPayload<WizardEventType.ComponentInstanceDestroyed> {
  eventType: WizardEventType.ComponentInstanceDestroyed = WizardEventType.ComponentInstanceDestroyed;
  constructor(
    public context: WizardContext,
    public step: IWizardStep,
  ) {}
}

/** STEP STATE CHANGE: A step can change its state at different times. The state encodes several features like pristine / completed / skipped / valid and so on */
export class StepStateChangedEvent implements WizardEventPayload<WizardEventType.StepStateChanged> {
  eventType: WizardEventType.StepStateChanged = WizardEventType.StepStateChanged;
  tags: string[];
  constructor(
    public step: IWizardStep,
    public prevState: StepState,
    public nextState: StepState,
  ) {
    this.tags = getTagsFromState(nextState);
  }
}

/** STEP CANNOT BE ENTERED: If a Guard stops a step from entering this event will be fired */
export class StepCannotBeEnteredEventPayload<D = CannotBeEnteredData> implements WizardEventPayload<WizardEventType.StepCannotBeEntered> {
  eventType: WizardEventType.StepCannotBeEntered = WizardEventType.StepCannotBeEntered;
  constructor(
    public step: IWizardStep,
    public reason: CannotBeEnteredReason,
    public data: D,
  ) {}
}

export type StepCannotBeEnteredEvent = PreventableEvent<StepCannotBeEnteredEventPayload>;

export class StepCannotBeSkippedEvent implements WizardEventPayload<WizardEventType.StepCannotBeSkipped> {
  eventType: WizardEventType.StepCannotBeSkipped = WizardEventType.StepCannotBeSkipped;
  constructor(
    public unskippableStep: IWizardStep,
    public targetStep: IWizardStep,
  ) {}
}

export class GuardEvaluationEvent implements WizardEventPayload<WizardEventType.GuardEvaluation> {
  eventType: WizardEventType.GuardEvaluation = WizardEventType.GuardEvaluation;
  constructor(
    public step: IWizardStep,
    public guard: CanActivate,
    public navigation: Navigation,
    public navigationContext: WizardNavigationContext,
  ) {}
}

export class ValidateStepBeforeNavigationEvent implements WizardEventPayload<WizardEventType.ValidateStepBeforeNavigation> {
  eventType: WizardEventType.ValidateStepBeforeNavigation = WizardEventType.ValidateStepBeforeNavigation;
  constructor(
    public controller: IWizardController,
    public navigationType: WizardNavigationContext['type'],
    public direction: StepDirection,
    public fromStep?: IWizardStep,
  ) {}
}

export class StepNavigationPreventedEvent implements WizardContextualEventPayload<WizardEventType.StepNavigationPrevented> {
  eventType: WizardEventType.StepNavigationPrevented = WizardEventType.StepNavigationPrevented;
  constructor(
    public context: WizardContext,
    public navigationType: WizardNavigationContext['type'],
  ) {}
}

export class StepInvalidOnNavigationEvent implements WizardContextualEventPayload<WizardEventType.StepInvalidOnNavigation> {
  eventType: WizardEventType.StepInvalidOnNavigation = WizardEventType.StepInvalidOnNavigation;
  constructor(
    public context: WizardContext,
    public navigationType: WizardNavigationContext['type'],
  ) {}
}

export class WizardBeforeNavigationEvent
  implements
    WizardEventPayload<
      WizardEventType.BeforeNextStepNavigation | WizardEventType.BeforePrevStepNavigation | WizardEventType.BeforeJumpNavigation
    >
{
  constructor(
    public eventType:
      | WizardEventType.BeforeNextStepNavigation
      | WizardEventType.BeforePrevStepNavigation
      | WizardEventType.BeforeJumpNavigation,
    public step: IWizardStep,
    public navigationContext?: WizardNavigationContext,
    public fromStep?: IWizardStep,
  ) {}
}

/** ENTER STEP: Send everytime a step is being entered. Type determines if next/prev buttons or progressbar jump was used or if the step was the initial step of the current progress */
export enum StepChangeType {
  NEXT = 'NEXT',
  PREV = 'PREV',
  JUMP = 'JUMP',
  START = 'START',
  DIRECT_NAVIGATION = 'DIRECT_NAVIGATION',
}

export enum StepDirection {
  FORWARD = 'FORWARD',
  BACKWARD = 'BACKWARD',
}

export const NavigationEventMap = new Map<StepChangeType, WizardEventType>([
  [StepChangeType.NEXT, WizardEventType.BeforeNextStepNavigation],
  [StepChangeType.PREV, WizardEventType.BeforePrevStepNavigation],
  [StepChangeType.JUMP, WizardEventType.BeforeJumpNavigation],
]);

export enum CannotBeEnteredReason {
  DEPENDENCY_UNFULFILLED = 'DEPENDENCY_UNFULFILLED',
  REQUEST_SKIP = 'REQUEST_SKIP',
}

export type DependencyGuardData = {
  route: ActivatedRouteSnapshot;
  state: RouterStateSnapshot;
  dependencyResults: ConditionCheckResult;
  navigationContext?: WizardNavigationContext;
};
export type CannotBeEnteredData = DependencyGuardData | object;
